import React, { useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import {
	uploadVehiculeDocuments,
	deleteVehiculeDocument,
} from "../../../store/actions/entiteActions"
import { COLORS } from "../../../utilities/Constants"
import CustomModal from "../../basics/interface/CustomModal"
import FileRenderer, { AddFileTile } from "../../others/FileRenderer"

const formatSize = (bytes) =>
	bytes < 1000000
		? `${(bytes / 1000).toFixed(0)} Ko`
		: `${(bytes / 1000000).toFixed(1)} Mo`

const styles = {
	subGroupLabel: {
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		marginTop: 16,
		marginBottom: 10,
	},
	notice: { color: COLORS.textLight, fontSize: 13, padding: "6px 2px" },
	row: {
		display: "flex",
		flexWrap: "wrap",
		gap: 10,
		alignItems: "flex-start",
	},
	pendingItem: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		padding: "8px 0",
		borderBottom: `1px solid ${COLORS.border}`,
		fontSize: 13,
	},
	pendingName: {
		flex: 1,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		color: COLORS.text,
	},
	pendingSize: { fontSize: 12, color: COLORS.textLight, flexShrink: 0 },
}

// Section Documents d'un véhicule : carte grise + fichiers "autres".
// L'affichage, la lecture (viewer/scroller AppPhotosViewer) et la suppression
// sont délégués à `FileRenderer` — même rendu que la fiche atelier. Upload
// immédiat après modal de confirmation.
const VehiculeDocumentsV2 = ({
	vehiculeId,
	initialFiles = [],
	initialCarteGrise = { saved: false, url: "" },
	isNew = false,
	onDocsChange = () => {},
}) => {
	const dispatch = useDispatch()
	const [files, setFiles] = useState(initialFiles || [])
	const [carteGrise, setCarteGrise] = useState(
		initialCarteGrise || { saved: false, url: "" },
	)
	const [pending, setPending] = useState(null) // { type, list: File[] }
	const [uploading, setUploading] = useState(false)
	const refFiles = useRef(null)
	const refCarteGrise = useRef(null)

	const applyVehicule = (veh) => {
		if (!veh) return
		const f = veh.files || []
		const cg = veh.carte_grise || { saved: false, url: "" }
		setFiles(f)
		setCarteGrise(cg)
		// Remonte au parent pour que la réouverture du véhicule reparte de la
		// donnée à jour (sinon fichiers vides tant que le client n'est rechargé).
		onDocsChange({ files: f, carte_grise: cg })
	}

	const onFilesChosen = (e) => {
		const list = Array.from(e.target.files || [])
		e.target.value = ""
		if (list.length) setPending({ type: "files", list })
	}
	const onCarteGriseChosen = (e) => {
		const f = e.target.files && e.target.files[0]
		e.target.value = ""
		if (f) setPending({ type: "carte_grise", list: [f] })
	}

	const confirmUpload = () => {
		setUploading(true)
		const payload =
			pending.type === "carte_grise"
				? { carteGrise: pending.list[0] }
				: { files: pending.list }
		dispatch(uploadVehiculeDocuments(vehiculeId, payload)).then((veh) => {
			setUploading(false)
			applyVehicule(veh)
			setPending(null)
		})
	}

	// Suppression déléguée à FileRenderer (modal de confirmation intégrée). Pas
	// de `postDelete` → FileRenderer ferme lui-même la modal après l'appel.
	const deletePhoto = (fileId) =>
		dispatch(
			deleteVehiculeDocument(vehiculeId, { type: "file", fileId }),
		).then(applyVehicule)
	// La carte grise est un slot unique : supprimée par type, l'id est ignoré.
	const deleteCarteGrise = () =>
		dispatch(
			deleteVehiculeDocument(vehiculeId, { type: "carte_grise" }),
		).then(applyVehicule)

	if (isNew) {
		return (
			<>
				<div style={styles.subGroupLabel}>Documents</div>
				<div style={styles.notice}>
					Enregistrez le véhicule pour pouvoir ajouter des documents.
				</div>
			</>
		)
	}

	return (
		<>
			{/* Carte grise (slot unique, même visuel que les photos mais sans
			    galerie/scroll) */}
			<div style={styles.subGroupLabel}>Carte grise</div>
			<div style={styles.row}>
				{carteGrise.saved ? (
					<FileRenderer
						files={[
							{
								url: carteGrise.url,
								name: "Carte grise",
								saved: true,
							},
						]}
						canDelete
						deleteFile={deleteCarteGrise}
					/>
				) : (
					<AddFileTile
						onClick={() => refCarteGrise.current.click()}
					/>
				)}
			</div>

			{/* Fichiers "autres" (photos) — galerie + viewer via FileRenderer */}
			<div style={styles.subGroupLabel}>Fichiers</div>
			<div style={styles.row}>
				<FileRenderer
					files={files}
					canDelete
					deleteFile={deletePhoto}
				/>
				<AddFileTile onClick={() => refFiles.current.click()} />
			</div>

			<input
				ref={refFiles}
				type="file"
				multiple
				style={{ display: "none" }}
				onChange={onFilesChosen}
			/>
			<input
				ref={refCarteGrise}
				type="file"
				style={{ display: "none" }}
				onChange={onCarteGriseChosen}
			/>

			{pending && (
				<CustomModal
					title={
						pending.type === "carte_grise"
							? "Ajouter cette carte grise ?"
							: "Ajouter ces fichiers ?"
					}
					onClose={uploading ? () => {} : () => setPending(null)}
					showCloseButton={!uploading}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: () => setPending(null),
							disabled: uploading,
						},
						{
							title: uploading ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : (
								"Confirmer"
							),
							styleName: "primary",
							onClick: confirmUpload,
							disabled: uploading,
						},
					]}
				>
					<div>
						{pending.list.map((f, i) => (
							<div key={i} style={styles.pendingItem}>
								<span style={styles.pendingName}>{f.name}</span>
								<span style={styles.pendingSize}>
									{formatSize(f.size)}
								</span>
							</div>
						))}
					</div>
				</CustomModal>
			)}
		</>
	)
}

export default VehiculeDocumentsV2
